import styled from '@emotion/styled';
import classNames from 'classnames';
import React, { TdHTMLAttributes } from 'react';
import { semantic_colors, text_styles } from '../../foundation';
import { buildCSSWithLength, getZIndex } from '../../styles';
import { CellSize, ColumnAlign } from '../table/types';
import { cell_size_css } from './styles';

interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  id?: string;
  fixed?: boolean;
  fixedColWidth?: number;
  isEmpty?: boolean;
  disabled?: boolean;
  align?: ColumnAlign;
  resize?: boolean;
  colSpan?: number;
  isRowLast?: boolean;
  cellDisabledStyle?: boolean;
  clickable?: boolean;
  selectable?: boolean;
  isColumnLast?: boolean;
  /** @default 'medium' */
  size?: CellSize;
  /** @default true */
  isTruncate?: boolean;
  /** @default true */
  isHugeContents?: boolean;
}

export const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  (
    {
      children,
      className,
      colSpan,
      id,
      valign = 'middle',
      size = 'medium',
      isTruncate = true,
      isHugeContents,
      width,
      ...props
    },
    ref,
  ) => {
    return (
      <StyledTableCell
        ref={ref}
        className={classNames(id ? `pds-table-cell-${id}` : 'pds-table-cell', className)}
        colSpan={colSpan}
        valign={valign}
        size={size}
        isTruncate={isTruncate}
        isHugeContents={isHugeContents}
        width={width}
        aria-disabled={props.disabled}
        {...props}
      >
        {children}
      </StyledTableCell>
    );
  },
);

const StyledTableCell = styled.td<{
  fixed?: boolean;
  fixedColWidth?: number;
  isEmpty?: boolean;
  disabled?: boolean;
  align?: ColumnAlign;
  resize?: boolean;
  isRowLast?: boolean;
  isColumnLast?: boolean;
  clickable?: boolean;
  selectable?: boolean;
  cellDisabledStyle?: boolean;
  valign: TableCellProps['valign'];
  size: CellSize;
  isTruncate?: boolean;
  wrapper?: boolean;
  isHugeContents?: boolean;
  width?: string | number;
}>`
  vertical-align: ${({ valign }) => valign};
  ${text_styles.Body_13_Regular};
  text-align: ${({ align }) => align ?? 'left'};
  ${({ disabled, cellDisabledStyle }) =>
    disabled && cellDisabledStyle
      ? `
          color: ${semantic_colors.content.disabled};
          > * {
            opacity: 0.4;
            color: ${semantic_colors.content.primary};
          }
  `
      : `
    color: ${semantic_colors.content.primary};
  `};
  ${({ isEmpty, size }) => (isEmpty ? 0 : cell_size_css[size])};
  border: 1px solid ${semantic_colors.border.primary};
  border-top: none;
  border-left: none;
  &:last-of-type {
    border-right: none;
  }
  ${({ fixed, fixedColWidth }) =>
    fixed &&
    `
     z-index: ${getZIndex('tableFixedCol')};
     position: sticky;
     ${buildCSSWithLength('left', fixedColWidth ?? 0)};
     background-color: ${semantic_colors.background.surface};`}
  ${({ resize }) =>
    resize &&
    `.ContentsWrapper {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }`}
     ${({ width, isTruncate }) =>
    width && isTruncate
      ? `
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: ${width}px;
        white-space: nowrap;
    `
      : ``}
  ${({ isHugeContents }) => (isHugeContents ? 'white-space: normal; word-break: break-word;' : '')};
  ${({ isRowLast }) => isRowLast && 'border-bottom: none;'};
  ${({ isColumnLast }) => isColumnLast && 'border-right: none;'};
  ${({ clickable, selectable, disabled }) =>
    disabled ? `cursor: not-allowed;` : clickable && `cursor: ${selectable ? 'pointer' : 'not-allowed'}`}

  &.pds-table-expanded-cell {
    background-color: ${semantic_colors.background.base};
  }
  &.pds-table-divider-cell {
    background-color: ${semantic_colors.background.base};
    padding: 2px;
  }
`;
